import type { ComponentRef, ReactElement, ReactNode } from 'react';
import { cloneElement, forwardRef } from 'react';
import type { ChakraProps } from '@chakra-ui/react';
import { Box, Center, Flex, Grid, Text } from '@chakra-ui/react';

import { BaseLayout } from './BaseLayout';

const DEFAULT_PADDING = 7 as const;

export interface IllustrationLayoutProps extends ChakraProps {
  title?: ReactNode;
  subTitle?: ReactNode;
  titleAction?: ReactNode;
  renderHeader?: ReactElement;
  renderFooter?: ReactElement;
  Illustration: ReactElement;
  children: ReactNode;
  navigation: ReactNode;
}

export const BaseIllustrationLayout = forwardRef<ComponentRef<'div'>, IllustrationLayoutProps>(
  (
    {
      title,
      subTitle,
      titleAction,
      children,
      renderHeader,
      renderFooter,
      Illustration,
      navigation,
      ...chakraProps
    },
    ref,
  ) => {
    return (
      <BaseLayout navigation={navigation} {...chakraProps}>
        <Grid
          templateColumns={{ base: '1fr', xl: '1.5fr 1fr' }}
          templateRows={renderFooter ? '1fr auto' : undefined}
          height='100%'>
          <Flex flexDir='column' p={DEFAULT_PADDING} overflow='auto' ref={ref} {...chakraProps}>
            {renderHeader}
            <Flex
              flexDir='column'
              maxW={title ? 'xl' : 'full'}
              mx='auto'
              mt={10}
              flex={1}
              pos='relative'>
              {title && (
                <Flex justifyContent='space-between'>
                  <Text as='h1' variant='heading1' mb={2}>
                    {title}
                  </Text>
                  {titleAction}
                </Flex>
              )}
              {subTitle && (
                <Text variant='lead' mb={10} width={titleAction ? '80%' : 'full'}>
                  {subTitle}
                </Text>
              )}
              {children}
            </Flex>
          </Flex>
          {renderFooter && (
            <Center
              w='full'
              bgColor='white'
              boxShadow='card'
              gridRow='2 / span 1'
              h={['75px', '100px']}
              pos={{ base: 'sticky', md: 'revert' }}
              bottom={{ base: 0, md: 'revert' }}>
              {renderFooter}
            </Center>
          )}
          {Illustration && (
            <Box gridRow={renderFooter ? '1 / span 2' : undefined}>
              {cloneElement(Illustration as ReactElement<Record<string, unknown>>, {
                display: { base: 'none', xl: 'flex' },
              })}
            </Box>
          )}
        </Grid>
      </BaseLayout>
    );
  },
);
